import { formatEther } from 'ethers/lib/utils';
import { useTranslation } from 'next-i18next';
import { useMemo, VFC } from 'react';

import { ListingType } from '@/apis/aniftyBackend/mintAsks/interface';
import CreationCard from '@/components/CreationCard';
import routes from '@/config/routes';
import { DEFAULT_PROFILE_IMAGE_PATH } from '@/constants/images';
import generateSquareImageURL from '@/utils/generateSquareImageURL';
import getThumbnailURL from '@/utils/getThumbnailURL';

import styles from './CreationItem.module.scss';

type Props = {
  item: {
    aniftyTokenID: number;
    creationID?: number;
    imageURL: string;
    tokenName: string;
    tokenNameJA: string | null;
    width?: number;
    height?: number;
    cropStartX?: number;
    cropStartY?: number;
    cropSize?: number;
    creatorProfile: {
      displayNameEN?: string;
      displayNameJP?: string | null;
      profilePicUrl?: string | null;
    };
    price?: string;
    totalTokenAmount?: number;
    availableTokenAmount?: number;
    listingType: ListingType;
    highestBidPrice: string | null;
  };
};

const CreationItem: VFC<Props> = ({ item }: Props) => {
  const { i18n } = useTranslation();

  const creatorName =
    item.creatorProfile.displayNameJP && i18n.language === 'ja'
      ? item.creatorProfile.displayNameJP
      : item.creatorProfile.displayNameEN;

  const creatorImage = !item.creatorProfile.profilePicUrl
    ? DEFAULT_PROFILE_IMAGE_PATH
    : generateSquareImageURL(item.creatorProfile.profilePicUrl, 330);

  const price = useMemo(() => {
    if (item.listingType === ListingType.Auction && item.highestBidPrice) {
      return formatEther(item.highestBidPrice);
    } else {
      return item.price ? formatEther(item.price) : null;
    }
  }, [item.price, item.highestBidPrice]);

  const tokenLink = useMemo(() => {
    if (item.aniftyTokenID === 85) {
      return 'https://opensea.io/assets/0x21545e9f21844758f6d4755230d9d7c62e08b449/85';
    }

    if (item.creationID) {
      return routes.creationDetail(item.creationID.toString());
    }

    // This should not happen, but somehow there is no creation associated with this token
    return routes.marketplace;
  }, [item]);

  const tokenName =
    item.tokenNameJA && i18n.language === 'ja'
      ? item.tokenNameJA
      : item.tokenName;

  return (
    <div className={styles.usersItem}>
      <CreationCard
        link={tokenLink}
        creationImageUrl={item.imageURL}
        thumbnailUrl={getThumbnailURL(item.imageURL, {
          width: item.width,
          height: item.height,
          cropStartX: item.cropStartX,
          cropStartY: item.cropStartY,
          cropSize: item.cropSize,
        })}
        creationName={tokenName}
        creatorName={creatorName}
        creatorProfileImageUrl={creatorImage}
        price={price}
        listingType={item.listingType}
      />
    </div>
  );
};

export default CreationItem;
